import { Component, Injector, OnInit } from '@angular/core';
import { Model } from 'survey-core';
import {
  AssociationSettingsApiService,
  FormsApiService, MemberApiService, NrdsAssociationsApiService, OfficesApiService, PeopleApiService,
  PortalConfigService,
  SnackbarService
} from '@tangilla/tg-ng-core';
import { ITheme } from 'survey-core/typings/packages/survey-core/src/themes';
import { finalize, firstValueFrom } from 'rxjs';
import * as SurveyCore from 'survey-core';
import Inputmask from 'inputmask';
// @ts-ignore
import { inputmask } from 'surveyjs-widgets';
// @ts-ignore
import markdownit from 'markdown-it';
import { formTheme } from './form-theme';
import { FormProcessorService } from './services/form-processor.service';
import { FormConfigurationService } from './complex-forms-services/form-configuration-service';
import { SurveyService } from '../../core/services/survey/survey.service';
import { initializeFunctions } from './functions/initialize-functions';
import { ErrorsService } from '../../core/services/utils/errors.service';
import { FormJsonService } from '../../core/services/survey/form-json.service';
import { SurveyPayloadService } from '../../core/services/survey/survey-payload.service';
import { SurveyNavigationErrorsService } from '../../core/services/survey/survey-navigation-errors.service';
import { CommitteeUtilsService } from '../../core/services/utils/committee.utils.service';
import { OfficeRosterUtilsService } from '../../core/services/utils/office-roster.utils.service';
import { RecordMappingService } from '../../core/services/utils/record-mapping.service';

inputmask(SurveyCore);

@Component({
  selector: 'tgf-form-processor',
  templateUrl: './form-processor.component.html',
  styleUrls: ['./form-processor.component.scss']
})
export class FormProcessorComponent implements OnInit {
  isLoading = false;
  isLoadingForm = false;
  isLoadingModelConfiguration = false;
  isLoadingComplete = false;

  formConfigurationService?: FormConfigurationService;

  constructor(private portalConfigService: PortalConfigService,
              private formsApiService: FormsApiService,
              private injector: Injector,
              public formJsonService: FormJsonService,
              private recordMappingService: RecordMappingService,
              public surveyService: SurveyService,
              public errorsService: ErrorsService,
              private formProcessorService: FormProcessorService,
              private snackbarService: SnackbarService,
              private surveyPayloadService: SurveyPayloadService,
              private memberAssociationSettingsApiService: AssociationSettingsApiService,
              private surveyNavigationErrorsService: SurveyNavigationErrorsService,
              private committeeUtilsService: CommitteeUtilsService,
              private officeRosterUtilsService: OfficeRosterUtilsService
  ) {
  }

  ngOnInit() {
    this.isLoading = true;
    this.initializeSurvey();
    this.isLoading = false;
  }

  private async initializeSurvey() {
    this.setSurveyGlobals();

    await this.setSurveyModel();

    this.portalConfigService.portalConfig.association_colors;
    this.surveyService.surveyModel.ignoreValidation = !!this.surveyNavigationErrorsService.ignoreValidations;

    (window as any).survey = this.surveyService.surveyModel;

    this.isLoadingModelConfiguration = false;
  }

  setSurveyGlobals() {
    const formData = this.formJsonService.tgForm;

    if (formData.person_id) {
      this.surveyService.personId = formData.person_id;
    }
    if (formData.member_nrds_id) {
      this.surveyService.memberId = formData.member_nrds_id;
    }
    if (formData.office_nrds_id) {
      this.surveyService.officeId = formData.office_nrds_id;
    }
    if (formData.search_by_corp_license) {
      this.surveyService.searchByCorpLicense = formData.search_by_corp_license;
    }
    if (formData.response_header_id) {
      this.surveyService.responseHeaderId = formData.response_header_id;
    }
  }

  async setSurveyModel() {
    const model = new Model(this.formJsonService.tgForm?.form_json);

    this.setMarkdownToModel(model);
    this.setThemeToModel(model);
    this.formProcessorService.processFormParameters(model);
    this.formProcessorService.processFormPropertiesByType(model);
    model.questionErrorLocation = 'bottom';

    await this.processTangillaCustomBehaviors(model);
    this.surveyService.surveyModel = model;
    await this.initializeFormConfigurationService();

    this.surveyService.surveyModel.onComplete.add(this.onSurveyComplete());
    this.surveyService.surveyModel.setVariable('current-year', new Date().getFullYear());

    const memberServiceAssociationSettings = await (firstValueFrom(this.memberAssociationSettingsApiService.get()));
    this.surveyService.isThompsonState = memberServiceAssociationSettings?.sync_offices === 'Thompson';
  }

  async processTangillaCustomBehaviors(model: Model) {
    const questions = model.getAllQuestions();
    const committeeApplicationsQuestion = questions.find(q => q['jsonObj']['mappedField'] === 'CommitteeApplications');
    const officeRosterQuestion = questions.find(q => q['jsonObj']['mappedField'] === 'OfficeRoster');

    if (this.surveyService.personId && !this.formJsonService.tgForm.type) {
      await this.recordMappingService.processRecordMapping(model, this.surveyService.personId);
    }
    if (officeRosterQuestion || committeeApplicationsQuestion) {
      const formData = this.formJsonService.tgForm;

      if (formData.form_title) {
        this.officeRosterUtilsService.formTitle = formData.form_title;
        model.setVariable('form_title', formData.form_title);
      }
      if (committeeApplicationsQuestion) {
        await this.committeeUtilsService.loadCommitteeTerms(this.surveyService.personId, model);
      }
      if (officeRosterQuestion) {
        await this.officeRosterUtilsService.loadOfficeRoster(this.surveyService.officeId, this.surveyService.memberId, this.surveyService.isPreview, model);
      }
    }
  }

  async initializeFormConfigurationService() {
    if (this.formJsonService.tgForm.type) {
      const module = await import('./complex-forms-services/complex-forms.module');
      this.formConfigurationService = this.injector.get(module.ComplexFormsModule.getService(this.formJsonService.tgForm.type));
      this.surveyService.formSource = this.formJsonService.tgForm.type;
      this.formConfigurationService!.initializeForm();
    }
  }

  setMarkdownToModel(model: any) {
    const converter = markdownit({
      html: true
    });
    model.onTextMarkdown.add((_: any, options: any) => {
      options.html = converter.renderInline(options.text);
    });
  }

  setThemeToModel(model: any) {
    const theme = this.getTheme();
    model.applyTheme(theme);
  }

  onSurveyComplete() {
    const that = this;
    const formJsonService = that.formJsonService;

    return () => {
      if (!that.surveyService.isPreview) {
        that.isLoadingComplete = true;
        const body = that.surveyPayloadService.getSurveyPayload();

        return that.formsApiService.completeForm(formJsonService.tgForm.form_id, body)
          .pipe(finalize(() => that.isLoadingComplete = false))
          .subscribe({
            next: (_: any) => that.snackbarService.success('Form has been succesfully submited.'),
            error: (error: any) => {
              this.errorsService.processError(error);
            }
          });
      } else {
        return;
      }
    };
  }

  private getTheme(): ITheme {
    const portalConfig = this.portalConfigService.portalConfig;

    return formTheme(portalConfig);
  }

  isItLoading() {
    return this.isLoading ||
      this.isLoadingForm ||
      this.isLoadingModelConfiguration ||
      this.surveyService.isLoading ||
      this.isLoadingComplete ||
      this.committeeUtilsService.isLoading ||
      this.officeRosterUtilsService.isLoading ||
      this.recordMappingService.isLoading;
  }
}

initializeFunctions();
